import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Table, Button, Checkbox, Dropdown, Card } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import formatNumber from "../../../../utils/formatNumber";
import {
	setPaymentTableFilter,
	setSelectedCustomerData,
} from "../../../../actions/paymentTableFilter/paymentTableFilterAction";

const CustomerAgingTbl = ({
	customerAgingData,
	customerAgingLoading,
	OnhandleTableSortColumn,
	isEmployee,
	currency,
	isReceivable,
}) => {
	const dispatch = useDispatch();
	const resizingRef = useRef(null);
	// const navigate = useNavigate();
	const [selectedRowData, setSelectedRowData] = useState(null);
	const [totalPendingLCY, setTotalPendingLCY] = useState(0);
	const [totalPending, setTotalPending] = useState(0);
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageNo, setPageNo] = useState(1);
	const [columnPreferences, setColumnPreferences] = useState({});
	const [hoveredResizeColumn, setHoveredResizeColumn] = useState(null);
	const [activeResizeColumn, setActiveResizeColumn] = useState(null);
	const minColumnWidth = 90;
	const resizeCursor =
		"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none'><path d='M10 4V20' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M14 4V20' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M7 12H2' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M4.5 9.5L2 12L4.5 14.5' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'/><path d='M17 12H22' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round'/><path d='M19.5 9.5L22 12L19.5 14.5' stroke='%23FFFFFF' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'/></svg>\") 14 14, col-resize";

	useEffect(() => {
		const handleMouseMove = (event) => {
			const resizeState = resizingRef.current;

			if (!resizeState) {
				return;
			}

			const nextWidth = Math.max(
				resizeState.minWidth,
				resizeState.startWidth + (event.clientX - resizeState.startX),
			);

			setColumnPreferences((prev) => ({
				...prev,
				[resizeState.columnKey]: {
					...prev[resizeState.columnKey],
					width: nextWidth,
				},
			}));
		};

		const handleMouseUp = () => {
			if (resizingRef.current) {
				setActiveResizeColumn(null);
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
				resizingRef.current = null;
			}
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);

		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, []);

	const parseAmount = (value) => parseFloat((value || "0").toString().replace(/,/g, "")) || 0;

	const amountLabel = isEmployee
		? isReceivable
			? "Received Amount LCY (INR)"
			: "Pending Amount LCY (INR)"
		: isReceivable
			? `Received Amount (${currency})`
			: `Pending Amount (${currency})`;

	const columnDefinitions = [
		{
			key: "customerOrganization",
			label: "Customer Name",
			title: "Customer Name",
			dataIndex: "customerOrganization",
			sorter: (a, b) => (a.customerOrganization || "").localeCompare(b.customerOrganization || ""),
			showSorterTooltip: false,
			width: 250,
			defaultFixed: true,
			render: (_i, row) => {
				return (
					<div>
						<label className="cust_po_no_lbl">{row.customerOrganization}</label>
					</div>
				);
			},
		},
		{
			key: "amount",
			label: amountLabel,
			title: amountLabel,
			dataIndex: isEmployee
				? isReceivable
					? "totalReceivedAmount_LCY"
					: "totalPendingAmount_LCY"
				: isReceivable
					? "totalReceivedAmount"
					: "totalPendingAmount",
			sorter: (a, b) => {
				const field = isEmployee
					? isReceivable
						? "totalReceivedAmount_LCY"
						: "totalPendingAmount_LCY"
					: isReceivable
						? "totalReceivedAmount"
						: "totalPendingAmount";
				return parseAmount(a[field]) - parseAmount(b[field]);
			},
			showSorterTooltip: false,
			width: 160,
			render: (_text, record) => {
				const field = isEmployee
					? isReceivable
						? "totalReceivedAmount_LCY"
						: "totalPendingAmount_LCY"
					: isReceivable
						? "totalReceivedAmount"
						: "totalPendingAmount";
				return formatNumber(parseAmount(record[field]));
			},
		},
		{
			key: "first",
			label: "0-10 Days",
			title: "0-10 Days",
			dataIndex: "first",
			sorter: (a, b) => parseAmount(a.first) - parseAmount(b.first),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.first !== "" ? formatNumber(parseAmount(row.first)) : "-"}</div>,
		},
		{
			key: "second",
			label: "11-15 Days",
			title: "11-15 Days",
			dataIndex: "second",
			sorter: (a, b) => parseAmount(a.second) - parseAmount(b.second),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.second !== "" ? formatNumber(parseAmount(row.second)) : "-"}</div>,
		},
		{
			key: "third",
			label: "16-30 Days",
			title: "16-30 Days",
			dataIndex: "third",
			sorter: (a, b) => parseAmount(a.third) - parseAmount(b.third),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.third !== "" ? formatNumber(parseAmount(row.third)) : "-"}</div>,
		},
		{
			key: "fourth",
			label: "31-45 Days",
			title: "31-45 Days",
			dataIndex: "fourth",
			sorter: (a, b) => parseAmount(a.fourth) - parseAmount(b.fourth),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.fourth !== "" ? formatNumber(parseAmount(row.fourth)) : "-"}</div>,
		},
		{
			key: "fifth",
			label: "46-60 Days",
			title: "46-60 Days",
			dataIndex: "fifth",
			sorter: (a, b) => parseAmount(a.fifth) - parseAmount(b.fifth),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.fifth !== "" ? formatNumber(parseAmount(row.fifth)) : "-"}</div>,
		},
		{
			key: "sixth",
			label: "61-75 Days",
			title: "61-75 Days",
			dataIndex: "sixth",
			sorter: (a, b) => parseAmount(a.sixth) - parseAmount(b.sixth),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.sixth !== "" ? formatNumber(parseAmount(row.sixth)) : "-"}</div>,
		},
		{
			key: "seventh",
			label: "76-90 Days",
			title: "76-90 Days",
			dataIndex: "seventh",
			sorter: (a, b) => parseAmount(a.seventh) - parseAmount(b.seventh),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.seventh !== "" ? formatNumber(parseAmount(row.seventh)) : "-"}</div>,
		},
		{
			key: "eighth",
			label: ">90 Days",
			title: ">90 Days",
			dataIndex: "eighth",
			sorter: (a, b) => parseAmount(a.eighth) - parseAmount(b.eighth),
			showSorterTooltip: false,
			width: 120,
			render: (_i, row) => <div>{row.eighth !== "" ? formatNumber(parseAmount(row.eighth)) : "-"}</div>,
		},
	];

	useEffect(() => {
		setColumnPreferences((prev) => {
			const next = {};

			columnDefinitions.forEach((column) => {
				next[column.key] = {
					visible: prev[column.key]?.visible ?? true,
					fixed: prev[column.key]?.fixed ?? Boolean(column.defaultFixed),
					width: prev[column.key]?.width ?? column.width,
				};
			});

			return next;
		});
	}, [isEmployee, currency, isReceivable]);

	useEffect(() => {
		const activeList = isReceivable
			? customerAgingData.customerAgeingModel_Received
			: customerAgingData.customerAgeingList;
		if (activeList?.length > 0) {
			setTotalPendingLCY(customerAgingData.total);
			setTotalPending(
				isReceivable ? customerAgingData.totalReceivedAmount : customerAgingData.totalPendingAmount,
			);
			setTotalRecords(customerAgingData.totalRecords);
			setPageNo(Number(customerAgingData.currentPage));
		} else {
			setTotalPendingLCY(0);
			setTotalPending(0);
			setTotalRecords(0);
			setPageNo(1);
		}
	}, [customerAgingData.customerAgeingList, customerAgingData.customerAgeingModel_Received]);

	//// handle Table sorting and pagination code
	const handleTableAscDes = (pagination, filters, sorter) => {
		const sortOrder = sorter.order;
		const sortField = sorter.field;
		OnhandleTableSortColumn({
			currentPage: pagination.current,
			sortColumn: sortField,
			orderBy: sortOrder === "ascend" ? "asc" : sortOrder === "descend" ? "desc" : "asc",
			table: "Customer Aging",
			isPaginationSorting: false,
		});
	};
	//// handle Table Row selection data
	const handleTableRowSelectedData = (orgName) => {
		setSelectedRowData(orgName);
		OnhandleTableSortColumn({
			currentPage: 1,
			sortColumn: "",
			orderBy: "asc",
			table: "Customer Aging",
			isPaginationSorting: true,
		});
		dispatch(setPaymentTableFilter(orgName));
		dispatch(setSelectedCustomerData(orgName));
	};

	const visibleColumnCount = Object.values(columnPreferences).filter((column) => column.visible).length;
	const allColumnsSelected = visibleColumnCount === columnDefinitions.length;
	const isSelectAllIndeterminate = visibleColumnCount > 0 && visibleColumnCount < columnDefinitions.length;

	const handleColumnVisibilityChange = (columnKey, checked) => {
		if (!checked && visibleColumnCount === 1) {
			return;
		}

		setColumnPreferences((prev) => {
			const next = {
				...prev,
				[columnKey]: {
					...prev[columnKey],
					visible: checked,
					fixed: checked ? (prev[columnKey]?.fixed ?? false) : false,
				},
			};

			if (checked) {
				const visibleColumns = columnDefinitions.filter(
					(column) => next[column.key]?.visible
				);
				const targetIndex = visibleColumns.findIndex(
					(column) => column.key === columnKey
				);
				const hasFixedColumnAfterTarget = visibleColumns.some(
					(column, index) => index > targetIndex && next[column.key]?.fixed
				);

				if (hasFixedColumnAfterTarget) {
					next[columnKey] = {
						...next[columnKey],
						fixed: true,
					};
				}
			}

			return next;
		});
	};

	const handleSelectAllColumns = (checked) => {
		setColumnPreferences((prev) => {
			const next = {};

			columnDefinitions.forEach((column, index) => {
				next[column.key] = {
					...prev[column.key],
					visible: checked ? true : index === 0,
					fixed: checked ? (prev[column.key]?.fixed ?? Boolean(column.defaultFixed)) : index === 0,
				};
			});

			return next;
		});
	};

	const handleColumnResizeStart = (event, columnKey) => {
		event.preventDefault();
		event.stopPropagation();

		const currentWidth =
			columnPreferences[columnKey]?.width ??
			columnDefinitions.find((column) => column.key === columnKey)?.width ??
			minColumnWidth;

		resizingRef.current = {
			columnKey,
			startX: event.clientX,
			startWidth: currentWidth,
			minWidth: minColumnWidth,
		};

		setActiveResizeColumn(columnKey);
		document.body.style.cursor = resizeCursor;
		document.body.style.userSelect = "none";
	};

  const handleColumnFixedChange = (columnKey, checked) => {
    setColumnPreferences((prev) => {
      const visibleColumns = columnDefinitions.filter(
        (column) => prev[column.key]?.visible
      );
      const targetIndex = visibleColumns.findIndex(
        (column) => column.key === columnKey
      );

      if (targetIndex === -1) {
        return prev;
      }

      const next = { ...prev };

      visibleColumns.forEach((column, index) => {
        if (checked) {
          if (index <= targetIndex) {
            next[column.key] = {
              ...next[column.key],
              fixed: true,
            };
          }
        } else if (index >= targetIndex) {
          next[column.key] = {
            ...next[column.key],
            fixed: false,
          };
        }
      });

      return next;
    });
  };

	const visibleColumns = columnDefinitions.filter(
		(column) => columnPreferences[column.key]?.visible
	);
	const lastFixedVisibleIndex = visibleColumns.reduce((lastIndex, column, index) => {
		return columnPreferences[column.key]?.fixed ? index : lastIndex;
	}, -1);
	const effectiveFixedColumnKeys = new Set(
		visibleColumns
			.filter((_column, index) => index <= lastFixedVisibleIndex)
			.map((column) => column.key)
	);

	const tableColumns = visibleColumns.map((column) => {
		const width = columnPreferences[column.key]?.width ?? column.width;

		return {
			...column,
			width,
			fixed: effectiveFixedColumnKeys.has(column.key) ? "left" : undefined,
			onHeaderCell: () => ({
				columnKey: column.key,
				width,
				onResizeStart: (event) => handleColumnResizeStart(event, column.key),
			}),
		};
	});

	const fixedLeftOffsets = {};
	let cumulativeLeft = 0;

	tableColumns.forEach((column) => {
		if (column.fixed === "left") {
			fixedLeftOffsets[column.key] = cumulativeLeft;
			cumulativeLeft += column.width || 120;
		}
	});

	const scrollWidth = tableColumns.reduce((total, column) => total + (column.width || 120), 0);

	const resizableTableComponents = {
		header: {
			cell: ({ children, onResizeStart, width, columnKey, ...restProps }) => {
				const isActive = activeResizeColumn === columnKey;
				const isHovered = hoveredResizeColumn === columnKey;

				return (
					<th
						{...restProps}
						style={{
							...restProps.style,
							width,
							minWidth: width,
							position: "relative",
						}}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								width: "100%",
								minHeight: "100%",
							}}>
							<span
								style={{
									flex: 1,
									minWidth: 0,
									paddingRight: "14px",
								}}>
								{children}
							</span>
						</div>
						<div
							onMouseDown={onResizeStart}
							onMouseEnter={() => setHoveredResizeColumn(columnKey)}
							onMouseLeave={() => setHoveredResizeColumn((current) => (current === columnKey ? null : current))}
							role="presentation"
							style={{
								position: "absolute",
								top: 0,
								right: -6,
								width: "12px",
								height: "100%",
								cursor: resizeCursor,
								zIndex: 3,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<div
								style={{
									width: isActive ? "3px" : "2px",
									height: isActive ? "68%" : isHovered ? "56%" : "40%",
									borderRadius: "999px",
									background: isActive ? "#3f3e3d" : isHovered ? "#56514d" : "transparent",
									boxShadow: isActive ? "0 0 0 3px rgba(63, 62, 61, 0.2)" : "none",
									transition: "all 0.18s ease",
								}}
							/>
						</div>
					</th>
				);
			},
		},
	};

	const columnSettingContent = (
		<div
			style={{
				background: "#ffffff",
				border: "1px solid #e3e8ef",
				borderRadius: "8px",
				boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
				width: "400px",
				maxWidth: "calc(100vw - 48px)",
				overflow: "hidden",
			}}
			onClick={(event) => event.stopPropagation()}>
			<div
				style={{
					padding: "14px 16px 12px",
					background: "#ffffff",
					borderBottom: "1px solid #edf1f5",
				}}>
				<div
					style={{
						fontWeight: 600,
						fontSize: "14px",
						color: "#2f3b52",
					}}>
					Customize Columns
				</div>
				<div
					style={{
						fontSize: "12px",
						color: "#7f8da3",
						marginTop: "4px",
						lineHeight: 1.5,
						paddingRight: "28px",
					}}>
					{visibleColumnCount} columns visible. Select columns to show and pin.
				</div>
			</div>
			<div
				style={{
					maxHeight: "360px",
					overflowY: "auto",
					padding: "8px 16px 14px",
				}}>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "minmax(0, 1fr) 72px",
						padding: "10px 0",
						borderBottom: "1px solid #edf1f5",
						color: "#8a97ab",
						fontSize: "11px",
						fontWeight: 600,
						letterSpacing: "0.04em",
						textTransform: "uppercase",
					}}>
					<span>Column</span>
					<span style={{ textAlign: "center" }}>Fixed</span>
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "minmax(0, 1fr) 72px",
						alignItems: "center",
						gap: "16px",
						padding: "12px 0",
						borderBottom: "1px solid #edf1f5",
						background: "#f8fafc",
					}}>
					<Checkbox
						checked={allColumnsSelected}
						indeterminate={isSelectAllIndeterminate}
						onChange={(event) => handleSelectAllColumns(event.target.checked)}>
						<span
							style={{
								color: "#2f3b52",
								fontWeight: 600,
								display: "inline-block",
								lineHeight: 1.4,
								paddingRight: "8px",
							}}>
							Select All
						</span>
					</Checkbox>
					<div />
				</div>
				{columnDefinitions.map((column) => {
					const preference = columnPreferences[column.key] || {
						visible: true,
						fixed: false,
					};

					return (
						<div
							key={column.key}
							style={{
								display: "grid",
								gridTemplateColumns: "minmax(0, 1fr) 72px",
								alignItems: "center",
								gap: "16px",
								padding: "12px 0",
								borderBottom: "1px solid #f1f4f8",
								background: preference.visible
									? "#ffffff"
									: "#fbfcfd",
							}}>
							<Checkbox
								checked={preference.visible}
								onChange={(event) => handleColumnVisibilityChange(column.key, event.target.checked)}>
								<span
									style={{
										color: preference.visible ? "#39465e" : "#98a3b5",
										fontWeight: 500,
										display: "inline-block",
										lineHeight: 1.4,
										wordBreak: "break-word",
										paddingRight: "8px",
									}}>
									{column.label}
								</span>
							</Checkbox>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: "100%",
									height: "100%",
									opacity: preference.visible ? 1 : 0.55,
								}}>
								<Checkbox
									checked={effectiveFixedColumnKeys.has(column.key)}
									disabled={!preference.visible}
									onChange={(event) => handleColumnFixedChange(column.key, event.target.checked)}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);

	const activeCustomerAgingData =
		(isReceivable ? customerAgingData.customerAgeingModel_Received : customerAgingData.customerAgeingList) || [];

	return (
		<Fragment>
			<div className="pay_section_3">
				<div className="row payment_tab">
					<div className="col-md-12 d-flex align-items-center justify-content-between">
						<div>
							<label className="analy_type">Customer Aging Details</label>
							<div
								style={{
									fontSize: "13px",
									color: "#7f8da3",
									marginTop: "4px",
									lineHeight: 1.5,
								}}>
								Note: Columns are resizable. Hover over any column edge and drag to adjust width.
							</div>
						</div>
						<div
							style={{
								display: "flex",
								paddingTop: "8px",
								paddingBottom: "2px",
                paddingRight: "8px",
							}}>
							<Dropdown overlay={columnSettingContent} trigger={["click"]} placement="bottomRight">
								<Button
									icon={<SettingOutlined style={{ marginBottom: "2px" }} />}
									style={{
										height: "40px",
										borderRadius: "50px",
										border: "1px solid #bfbfbf",
										boxShadow: "none",
										fontWeight: 600,
										paddingInline: "22px",
										background: "#ffffff",
										color: "#2f3b52",
										display: "flex",
										alignItems: "center",
									}}>
									Customize Columns
								</Button>
							</Dropdown>
						</div>
					</div>
				</div>
			</div>
			<Card className="pay_section_2">
				<div className="row">
					<div className="col-md-12">
						<Table
							bordered
							components={resizableTableComponents}
							scroll={{ x: Math.max(scrollWidth, 900) }}
							className="dashboard_tables payment_table"
							dataSource={activeCustomerAgingData}
							loading={customerAgingLoading}
							onChange={handleTableAscDes}
							pagination={{
								total: totalRecords,
								pageNo: pageNo,
								pageSize: 10,
								responsive: true,
								showSizeChanger: false,
								current: pageNo,
								onChange: (pageNum) => setPageNo(pageNum),
							}}
							columns={tableColumns}
							rowKey={(record, index) => `${record.customerOrganization || "customer"}-${index}`}
							onRow={(record) => {
								return {
									onClick: () => {
										handleTableRowSelectedData(record.customerOrganization);
									},
									className:
										record.customerOrganization === selectedRowData ? "tbl_selected_row" : "",
								};
							}}
							summary={() => {
								return (
									<Table.Summary.Row className="tbl_summary">
										{tableColumns.map((column, index) => {
											const isFixedColumn = column.fixed === "left";
											const fixedStyles = isFixedColumn
												? {
														position: "sticky",
														left: fixedLeftOffsets[column.key],
														zIndex: 2,
														background: "#8a8a8a",
													}
												: {};

											let cellValue = null;

											if (column.key === "customerOrganization") {
												cellValue = "Total";
											} else if (column.key === "amount") {
												cellValue = formatNumber(isEmployee ? totalPendingLCY : totalPending);
											}

											return (
												<Table.Summary.Cell
													key={column.key}
													index={index}
													style={{ fontWeight: "bold", ...fixedStyles }}>
													{cellValue}
												</Table.Summary.Cell>
											);
										})}
									</Table.Summary.Row>
								);
							}}
						/>
					</div>
				</div>
			</Card>
		</Fragment>
	);
};

export default CustomerAgingTbl;
